import { useState, useEffect } from 'react';
import { industryService } from '../../utils/apiService';
import logger from '../../utils/logger';

/**
 * Hook to manage loading of assessment questions based on step and sub-industry
 * 
 * Now uses sub_industry_id for the new schema, with fallback to legacy industry_name
 */
export const useAssessmentQuestions = ({
  step,
  subIndustryId,
  majorIndustry,
  region,
  setError
}) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (step !== 3) return;
      
      // Require either subIndustryId (new schema) or majorIndustry (legacy)
      if (!subIndustryId && !majorIndustry) return;

      setIsLoading(true);
      if (setError) {
        setError(null);
      }

      try {
        let result;
        
        // Prefer new schema with sub_industry_id
        if (subIndustryId) {
          result = await industryService.getQuestionsBySubIndustry(subIndustryId);
        } else {
          // Fallback to legacy method
          result = await industryService.getQuestions(majorIndustry, region);
        }

        if (result && result.success) {
          setQuestions(result.questions || result.data || []);
        } else {
          if (setError) {
            setError(result?.error || 'Failed to load questions');
          }
          setQuestions([]);
        }
      } catch (error) {
        logger.error('Questions loading error:', error);
        if (setError) {
          setError('Failed to load questions');
        }
        setQuestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    try {
      if (step === 3 && (subIndustryId || majorIndustry)) {
        fetchQuestions().catch(err => {
          logger.error('Async fetchQuestions error:', err);
          if (setError) {
            setError('Failed to load questions');
          }
          setIsLoading(false);
        });
      }
    } catch (error) {
      logger.error('useEffect error:', error);
      if (setError) {
        setError('Failed to load questions');
      }
      setIsLoading(false);
    }
  }, [step, subIndustryId, majorIndustry, region, setError]);

  return {
    questions,
    isLoading
  };
};


